import styled from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { IconButton } from "./Button";
import { Textarea } from "./Form";

export const FLOATING_PANEL_HEADER_GAP = 8;
export const FLOATING_PANEL_BOTTOM_CLEARANCE = 105;

interface FloatingPanelBoundariesOptions {
  viewportWidth: number;
  viewportHeight: number;
  panelWidth: number;
  panelHeight: number;
  headerBottom: number;
  bottomClearance?: number;
}

export const getFloatingPanelBoundaries = ({
  viewportWidth,
  viewportHeight,
  panelWidth,
  panelHeight,
  headerBottom,
  bottomClearance = FLOATING_PANEL_BOTTOM_CLEARANCE,
}: FloatingPanelBoundariesOptions) => {
  const minY = Math.max(0, Math.ceil(headerBottom) + FLOATING_PANEL_HEADER_GAP);

  return {
    minX: 0,
    maxX: Math.max(0, viewportWidth - panelWidth),
    minY,
    maxY: Math.max(minY, viewportHeight - panelHeight - bottomClearance),
  };
};

export const FloatingPanelSurface = styled.div<{
  $isExpanded?: boolean;
  $compactActions?: boolean;
}>`
  position: fixed;
  z-index: ${vars.zIndex.toast};
  display: flex;
  width: ${({ $isExpanded }) => ($isExpanded ? "520px" : "310px")};
  height: ${({ $isExpanded, $compactActions }) =>
    $isExpanded
      ? $compactActions
        ? "270px"
        : "302px"
      : $compactActions
        ? "491px"
        : "549px"};
  flex-direction: column;
  border: var(--inzeloop-ds-border-width-thick) solid
    ${vars.color.action.primary};
  border-radius: ${vars.radius.xl};
  background: ${vars.color.background.surface};
  padding: ${vars.spacing.sm} ${vars.spacing.lg} ${vars.spacing.lg};
  box-shadow: ${vars.shadow.dropdown};

  ${({ $isExpanded }) =>
    $isExpanded
      ? `
        top: 50%;
        left: 50%;
        right: auto;
        bottom: auto;
      `
      : `
        top: auto;
        right: 20px;
        bottom: 80px;
        left: auto;
        transform: none;
      `}
`;

export const FloatingPanelContent = styled.div<{ $isExpanded?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ $isExpanded }) =>
    $isExpanded ? vars.spacing.sm : vars.spacing.lg};
`;

export const FloatingPanelHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-bottom: calc(-1 * var(--inzeloop-ds-spacing-20px));
`;

export const FloatingPanelHeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${vars.spacing.lg};
`;

export const FloatingPanelTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${vars.spacing.sm};
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.px22};
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${vars.typography.lineHeight.px27};
  letter-spacing: -0.44px;
`;

export const FloatingPanelCloseButton = styled(IconButton).attrs({
  type: "button",
  size: "sm",
  variant: "ghost",
})`
  &:not(:disabled):hover {
    background: ${vars.color.action.primary};
    color: ${vars.color.text.inverse};
  }

  &:not(:disabled):active {
    background: ${vars.color.action.primaryPressed};
    color: ${vars.color.text.inverse};
  }
`;

export const FloatingPanelBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  flex-direction: column;
  color: ${vars.color.text.secondary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.xs};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.sm};
`;

export const FloatingPanelTextarea = styled(Textarea)<{
  $isExpanded?: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: ${({ $isExpanded }) => ($isExpanded ? "160px" : "368px")};
  flex-direction: column;
  align-self: stretch;
  gap: ${vars.spacing.xs};
  resize: none;
  border: ${vars.borderWidth.thin} solid ${vars.color.border.accent};
  border-radius: ${vars.radius.lg};
  padding: ${vars.spacing.lg};
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.lg};
`;

export const FloatingPanelDocumentList = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
  background: ${vars.color.background.neutral};
`;
